import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Building2, Users } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Three-way Personal / Corporate / Family switcher in the header.
 *
 * Persists selection to localStorage('align_mode') so the user lands in their
 * preferred mode on next login. Routes to /dashboard, /corporate, or /family.
 *
 * Props:
 *   active: 'personal' | 'corporate' | 'family' — controls visual selection
 */

const MODES = [
  { key: 'personal', icon: User, route: '/dashboard', labelKey: 'mode.personal' },
  { key: 'corporate', icon: Building2, route: '/corporate', labelKey: 'mode.corporate' },
  { key: 'family', icon: Users, route: '/family', labelKey: 'mode.family' }
];

export default function ModeToggle({ active, user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const switchTo = (mode) => {
    if (mode.key === active) return;
    
    if (mode.key !== 'personal' && user?.subscription?.tier === 'starter') {
      toast.error('Upgrade required: Collaborative workspaces (Corporate/Family) are only available on Pro & Elite tiers.');
      return;
    }

    try {
      localStorage.setItem('align_mode', mode.key);
    } catch (_) {
      // localStorage can throw in private-browsing on some browsers; ignore
    }
    navigate(mode.route);
  };

  // Layout: tight icon-only pills on mobile, icon+label from sm: breakpoint up.
  // This is what fixes the header overflow that caused the toggle to overlap
  // with the right-side utility buttons.
  const baseBtn = 'flex items-center gap-1.5 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm transition-all duration-200 font-medium';
  const activeBtn = 'bg-background shadow-sm text-foreground';
  const inactiveBtn = 'text-muted-foreground hover:text-foreground';

  return (
    <div
      className="inline-flex items-center bg-secondary/50 rounded-full p-0.5 sm:p-1 border border-border/60 flex-shrink-0"
      role="tablist"
      data-testid="mode-toggle"
    >
      {MODES.map((mode) => {
        const Icon = mode.icon;
        const isActive = mode.key === active;
        const label = t(mode.labelKey);
        return (
          <button
            key={mode.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={label}
            title={label}
            onClick={() => switchTo(mode)}
            className={`${baseBtn} ${isActive ? activeBtn : inactiveBtn}`}
            data-testid={`mode-toggle-${mode.key}`}
          >
            <Icon className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Helper to read the persisted mode preference. Used by routing/auth code
 * to redirect users back to their last-chosen mode after login.
 *
 * Returns one of 'personal' | 'corporate' | 'family' (defaults to 'personal').
 */
export function getStoredMode() {
  try {
    const m = localStorage.getItem('align_mode');
    if (m === 'corporate' || m === 'family' || m === 'personal') return m;
    return 'personal';
  } catch (_) {
    return 'personal';
  }
}

/**
 * Mapping from mode to the route a logged-in user should land on.
 */
export function modeToRoute(mode) {
  const found = MODES.find((m) => m.key === mode);
  return found ? found.route : '/dashboard';
}
